/**
 * 消息存储：按服务端稳定 ID 去重、按会话内序号数值排序。
 *
 * 约束来源：CONTRACT.md 第 42 行 "Events can duplicate; client dedup Message.id"、
 * 第 27 行 "Cursors canonical decimal strings, numeric/BigInt comparison"。
 * 因此排序与游标推进一律使用十进制数值比较，绝不用字符串排序。
 */

import { compareDecimal, isCanonicalDecimal } from '@support/platform';

import { SupportError } from './errors';
import type { Message, MessageVisibility } from './types';

export interface MergeResult {
  added: Message[];
  updated: Message[];
}

export class MessageStore {
  private readonly byId = new Map<string, Message>();

  private readonly ordered: Message[] = [];

  get size(): number {
    return this.ordered.length;
  }

  clear(): void {
    this.byId.clear();
    this.ordered.length = 0;
  }

  /** 合并一批消息（可能重复、可能乱序），返回本次新增/更新的消息。 */
  merge(items: Iterable<Message>): MergeResult {
    const added: Message[] = [];
    const updated: Message[] = [];
    for (const item of items) {
      const existing = this.byId.get(item.id);
      if (existing) {
        // 同 ID 内容一致则完全不动作；内容变化（例如上下文快照补全）则原地更新。
        if (shallowEqualMessage(existing, item)) continue;
        const index = this.ordered.indexOf(existing);
        if (index >= 0) this.ordered[index] = item;
        this.byId.set(item.id, item);
        updated.push(item);
        continue;
      }
      this.byId.set(item.id, item);
      this.ordered.push(item);
      added.push(item);
    }
    if (added.length > 0 || updated.length > 0) {
      this.ordered.sort((a, b) => compareDecimal(a.sequence, b.sequence));
    }
    return { added, updated };
  }

  get(id: string): undefined | Message {
    return this.byId.get(id);
  }

  list(): Message[] {
    return [...this.ordered];
  }

  /** 客户可见消息（排除内部备注）。 */
  visibleMessages(): Message[] {
    return this.ordered.filter((m) => m.visibility === 'public');
  }

  /**
   * 会话内最大序号（任意可见性）。工作台用。
   */
  latestSequence(): null | string {
    return this.maxSequence(() => true);
  }

  /**
   * 对客可见的最大序号高水位。
   * 客户不能用它反推内部备注数量；补拉时以它为 after，缺口不会泄漏内部活动。
   */
  latestPublicSequence(): null | string {
    return this.maxSequence((m) => m.visibility === 'public');
  }

  /** 补拉游标：取当前已见最大序号（数值比较，不回退）。 */
  cursorAfter(visibility: MessageVisibility = 'public'): null | string {
    return visibility === 'public' ? this.latestPublicSequence() : this.latestSequence();
  }

  /** 最旧序号，用于向上翻页 before=。 */
  oldestSequence(): null | string {
    let min: null | string = null;
    for (const m of this.ordered) {
      if (!isCanonicalDecimal(m.sequence)) {
        throw new SupportError({
          code: 0,
          kind: 'protocol',
          message: `消息序号不是规范化十进制字符串: ${JSON.stringify(m.sequence)}`,
        });
      }
      if (min === null || compareDecimal(m.sequence, min) < 0) min = m.sequence;
    }
    return min;
  }

  private maxSequence(predicate: (message: Message) => boolean): null | string {
    let max: null | string = null;
    for (const m of this.ordered) {
      if (!predicate(m)) continue;
      if (!isCanonicalDecimal(m.sequence)) {
        throw new SupportError({
          code: 0,
          kind: 'protocol',
          message: `消息序号不是规范化十进制字符串: ${JSON.stringify(m.sequence)}`,
        });
      }
      if (max === null || compareDecimal(m.sequence, max) > 0) max = m.sequence;
    }
    return max;
  }
}

/**
 * 判断服务端重复送达的两条消息是否内容等价。
 * 按字段值比较（不是引用比较），因此重复事件只被识别为重复而不会造成状态抖动。
 */
function shallowEqualMessage(a: Message, b: Message): boolean {
  return (
    a.clientMessageId === b.clientMessageId &&
    a.contentType === b.contentType &&
    a.id === b.id &&
    a.receivedAt === b.receivedAt &&
    a.sequence === b.sequence &&
    a.senderId === b.senderId &&
    a.senderType === b.senderType &&
    a.text === b.text &&
    a.visibility === b.visibility &&
    attachmentKey(a) === attachmentKey(b) &&
    contextKey(a) === contextKey(b)
  );
}

function attachmentKey(message: Message): string {
  return message.attachments.map((item) => item.id).join(',');
}

function contextKey(message: Message): string {
  const context = message.context;
  if (!context) return '';
  return [context.pageInstanceId, context.version, context.companyId ?? ''].join('|');
}